import { getFullImageUrl, getThumbnailUrl } from "../../utils/urlConfig";
import React, { useState, useEffect } from "react";
import { fetchAllLocations, deleteLocationById, assignLocationToparent } from "../../services/locationService";
import { generateLocationQRCode } from "../../services/qrcodeService";
import { toast } from 'react-toastify';
import styles from "./LocationTable.module.css";
// delete location
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faEdit } from "@fortawesome/free-solid-svg-icons";
import ConfirmModal from "../Modal/ConfirmModal";
import LocationModal from "./LocationModal";
import ImageModal from "../Modal/ImageModal";
import { FaInfoCircle, FaListUl, FaSitemap } from "react-icons/fa";
import LocationsTree from "./LocationsTree";
import QRCode from "react-qr-code";

/**
 * LocationTable Component
 * Displays a table of locations with pagination, search, sorting, and QR code generation.
 */
const LocationTable = () => {
  const [locations, setLocations] = useState([]); // All locations fetched from the API
  const [filteredLocations, setFilteredLocations] = useState([]); // Filtered and sorted locations for display
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [currentPage, setCurrentPage] = useState(1); // Current page
  const [pageSize] = useState(5); // Items per page
  const [searchQuery, setSearchQuery] = useState(""); // Search term
  const [sortField, setSortField] = useState(""); // Field to sort by
  const [sortOrder, setSortOrder] = useState(""); // Sort order ('asc' or 'desc')
  // delete location
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedLocationDeleteId, setSelectedLocationDeleteId] = useState(null);

  // View location details
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [viewMode, setViewMode] = useState("table"); // 'table' or 'tree'
  // image modal
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedImages, setSelectedImages] = useState([]);



  /**
   * Fetch all locations from the API and set the state.
   */
  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await fetchAllLocations();
      console.log(data);
      if (data != null) {
        setLocations(data);
        setFilteredLocations(data);
      }
    } catch (err) {
      setError(err.message);
      toast.error(`Failed to fetch locations: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Refreshes the list of locations by fetching the latest data
  const refreshLocations = () => {
    fetchData();
  }

  /**
   * Filter and sort locations whenever dependencies change.
   */

  useEffect(() => {
    let result = [...locations];

    // Filter by search query
    if (searchQuery) {
      result = result.filter(
        (location) =>
          location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          location.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort locations
    if (sortField) {
      result.sort((a, b) => {
        const aValue = a[sortField] || "";
        const bValue = b[sortField] || "";
        if (sortOrder === "asc") return aValue > bValue ? 1 : -1;
        if (sortOrder === "desc") return aValue < bValue ? 1 : -1;
        return 0;
      });
    }

    setFilteredLocations(result);
  }, [searchQuery, sortField, sortOrder, locations]);

  /**
   * Fetch data on initial render.
   */
  useEffect(() => {
    fetchData();
  }, []);

  /**
   * Get paginated data based on the current page and page size.
   */
  const paginatedLocations = filteredLocations.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  /**
   * Handle sorting of table columns.
   * @param {string} field - The field to sort by.
   */
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
  };

  /**
   * Handle page changes for pagination.
   * @param {number} page - The new page number.
   */
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  /**
 * Check if a string is a valid URL.
 * @param {string} url - The URL string to validate.
 * @returns {boolean} - True if valid, false otherwise.
 */
  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      console.log(e);
      return false;
    }
  };

  /**
 * Generate a QR code for a specific location.
 * @param {string} locationId - The ID of the location to generate a QR code for.
 */
  const handleGenerateClick = async (locationId) => {
    setLoading(true);
    try {
      console.log(locationId);
      const newQrCode = await generateLocationQRCode(locationId);
      // setLocations((prevState) => ({
      //   ...prevState,
      //   [locationId]: newQrCode, // Store the generated QR code for the specific location
      // }));
      fetchData();
      toast.success("QR Code generated successfully");
    } catch (error) {
      console.error("Failed to generate QR code:", error);
      toast.error("Failed to generate QR code");
    } finally {
      setLoading(false);
    }
  };


  // Handles opening the delete confirmation modal for the selected location
  const handleLocationDelete = async (locationId) => {
    // Sets the selected location ID for deletion and opens the modal
    setSelectedLocationDeleteId(locationId);
    setIsDeleteModalOpen(true);
  }

  // Closes the delete confirmation modal and resets the selected location ID
  const handleDeleteModalClose = () => {
    // Closes the modal and clears the selected location ID
    setIsDeleteModalOpen(false);
    setSelectedLocationDeleteId(null);
  };

  // Confirms the location deletion by calling the delete function and refreshing the locations list
  const handleDeleteConfirm = async () => {
    try {
      // Calls the async delete function to remove the location
      await deleteLocationById(selectedLocationDeleteId);
      toast.success("Location deleted successfully");
    } catch (error) {
      console.error("Failed to delete container:", error);
      toast.error("Failed to delete container");
    } finally {
      setLoading(false);
      refreshLocations();
    }
    console.log("Location deleted!");
    setIsDeleteModalOpen(false);
  };

  const handleUnlinkParent = async (locationId) => {
    if (!window.confirm("Are you sure you want to remove this location from its parent container?")) return;
    setLoading(true);
    try {
      await assignLocationToparent(locationId, -1);
      toast.success("Parent location unlinked successfully!");
      refreshLocations();
    } catch (error) {
      console.error("Failed to unlink parent container:", error);
      toast.error("Failed to unlink parent container");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Opens the LocationModal for the specified location.
   * @param {string} id - The ID of the location to view.
   */
  const handleViewDetails = (id) => {
    setSelectedLocationId(id);
    setIsModalOpen(true);
  };

  // Open the modal and set selected images and current image index
  const handleImageClick = (images, index) => {
    // If images array is provided, use it.
    // If only a single image path is available (legacy check), wrap it in an array.
    // Actually Location object has `images` array of objects (LocationImage) or `image` string.

    let imagesToDisplay = [];

    if (images && images.length > 0) {
      // Map LocationImage objects to imagePath strings for the modal
      imagesToDisplay = images.map(img => img.imagePath);
    }
    // Fallback for single image property if exists and no images array (though current backend seems to use images array)
    // The renderRows logic checks location.image as fallback.
    // We can't access `location.image` easily here without passing it.
    // So let's rely on what's passed to this function.

    // However, the renderRows call below passes `location.images` which is array of objects.
    // ImageModal expects array of strings (paths).

    // Let's refine the passed arguments in renderRows call.
    // But here we need to handle the conversion.
    if (Array.isArray(images) && images.length > 0 && typeof images[0] === 'object') {
      imagesToDisplay = images.map(img => img.imagePath);
    } else if (Array.isArray(images)) {
      imagesToDisplay = images;
    }

    // Checking if we got valid images
    if (imagesToDisplay.length > 0) {
      setSelectedImages(imagesToDisplay);
      setCurrentImageIndex(index);
      setIsImageModalOpen(true);
    } else {
      // Check for single image fallback? The loop below will handle passing logic.
      // If we are here, something is wrong or no images.
    }
  };

  // Go to the next image in the list
  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % selectedImages.length);
  };

  // Go to the previous image in the list
  const handlePreviousImage = () => {
    setCurrentImageIndex(
      (prevIndex) => (prevIndex - 1 + selectedImages.length) % selectedImages.length
    );
  };

  // Close the modal
  const handleCloseModal = () => {
    setIsImageModalOpen(false); // Close the modal
  };


  /**
   * Render table rows for locations.
   */
  const renderRows = () =>
    paginatedLocations.map((location) => {

      const qrCodeUrl = location.qrCode
        ? getFullImageUrl(location.qrCode)
        : null;

      return (
        <tr key={location.id} className={styles.row}>
          <td data-label="ID">{location.id}</td>
          <td data-label="Name">
            <span
              className={styles.locationName}
              onClick={() => handleViewDetails(location.id)}
              title="Click to view details"
            >
              {location.name}
            </span>
          </td>
          <td data-label="Description">{location.description}</td>
          <td data-label="Parent Container">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{location.parentLocation?.name || "-"}</span>
              {location.parentLocation && (
                <button
                  className={styles.unlinkSmallBtn}
                  onClick={() => handleUnlinkParent(location.id)}
                  title="Remove Parent Container"
                >
                  <FontAwesomeIcon icon={faTrash} style={{ fontSize: '0.8rem' }} />
                </button>
              )}
            </div>
          </td>
          <td data-label="Image">
            {location.images && location.images.length > 0 ? (
              <img
                src={getThumbnailUrl(location.images.find(img => img.isPrimary)?.thumbnailPath || location.images[0].thumbnailPath || location.images[0].imagePath)}
                alt={location.name}
                style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "4px", cursor: 'pointer' }}
                onClick={() => handleImageClick(location.images, 0)}
              />
            ) : location.image ? (
              <img
                src={getThumbnailUrl(location.image)}
                alt={location.name}
                style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "4px", cursor: 'pointer' }}
                onClick={() => handleImageClick([location.image], 0)} // Pass single image as array
              />
            ) : (
              <div style={{ width: "50px", height: "50px", background: "#eee", borderRadius: "4px" }}></div>
            )}
          </td>
          <td data-label="Details">
            <button
              onClick={() => handleViewDetails(location.id)}
              className={styles.assetDetailsButton} // Reuse consistent class if available, else check css
            >
              <i className="fas fa-eye"></i>
            </button>
          </td>
          <td data-label="QR Code" className={styles.tdqr}>
            {location.qrToken ? (
              <div style={{ background: 'white', padding: '4px', borderRadius: '6px', display: 'inline-block', border: '1px solid #e2e8f0' }}>
                <QRCode 
                  value={`${window.location.origin}/scan/${location.qrToken}`} 
                  size={48} 
                />
                <div style={{ fontSize: '9px', textAlign: 'center', marginTop: '2px', color: '#94a3b8', maxWidth: '56px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{location.qrToken}</div>
              </div>
            ) : qrCodeUrl && isValidUrl(qrCodeUrl) ? (
              <div style={{ position: "relative", display: "inline-block" }}>
                <img
                  src={getFullImageUrl(location.qrCode)}
                  alt="Container QR Code"
                  style={{ width: "100px", height: "100px" }}
                />
                <a
                  href={getFullImageUrl(location.qrCode)}
                  download
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: "rgba(0, 0, 0, 0.5)",
                    color: "#fff",
                    textAlign: "center",
                    lineHeight: "100px",
                    opacity: 0,
                    transition: "opacity 0.3s",
                    textDecoration: "none",
                    fontWeight: "bold",
                  }}
                  onMouseOver={(e) => (e.target.style.opacity = 1)}
                  onMouseOut={(e) => (e.target.style.opacity = 0)}
                >
                  Download
                </a>
              </div>
            ) : (
              <button
                className={`${styles.generateButton} ${loading ? styles.generateButtonLoading : ""}`}
                onClick={() => handleGenerateClick(location.id)}
                disabled={loading}
              >
                {loading ? (
                  <span className={styles.spinner}></span>
                ) : (
                  "Generate"
                )}
              </button>
            )}
          </td>
          <td data-label="Delete">
            <button className={styles.deleteButton} onClick={() => handleLocationDelete(location.id)}>
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </td>

          {/* Mobile Action Bar - Only visible on mobile via CSS */}
          <div className={styles["actions-mobile"]}>
            <button
              onClick={() => handleViewDetails(location.id)}
              className={styles.assetDetailsButton}
            >
              <i className="fas fa-eye"></i>
            </button>
            <div className={styles.mobileQrContainer}>
              <QRCode 
                value={`${window.location.origin}/scan/${location.qrToken}`} 
                size={32} 
              />
            </div>
            <button className={styles.deleteButton} onClick={() => handleLocationDelete(location.id)}>
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        </tr>
      );
    });

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error}</p>;

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>Containers</h2>
      {/* Search and Toggle Row */}
      <div className={styles.toolbar}>
        <input
          type="text"
          placeholder="Search by name or description"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />

        <div className={styles.viewToggle}>
          <button
            className={`${styles.toggleButton} ${viewMode === 'table' ? styles.activeToggle : ''}`}
            onClick={() => setViewMode('table')}
            title="Table View"
          >
            <FaListUl />
          </button>
          <button
            className={`${styles.toggleButton} ${viewMode === 'tree' ? styles.activeToggle : ''}`}
            onClick={() => setViewMode('tree')}
            title="Tree View"
          >
            <FaSitemap />
          </button>
        </div>
      </div>

      {/* Conditional Rendering: Table or Tree */}
      {viewMode === "table" ? (
        <>
          <div className={styles["table-wrapper-div"]}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th onClick={() => handleSort("id")}>ID</th>
                  <th onClick={() => handleSort("name")}>Name</th>
                  <th onClick={() => handleSort("description")}>Description</th>
                  <th onClick={() => handleSort("parentLocation")}>Parent Container</th>
                  <th onClick={() => handleSort("")}>Image</th>
                  <th>Details</th>
                  <th onClick={() => handleSort("")}>QR Code</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>{renderRows()}</tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className={styles.pagination}>
            {/* Back Button */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={styles.paginationButton}
            >
              &lt;
            </button>

            {/* Page Numbers */}
            {Array.from(
              { length: Math.ceil(filteredLocations.length / pageSize) },
              (_, i) => (
                <button
                  key={i + 1}
                  onClick={() => handlePageChange(i + 1)}
                  className={
                    currentPage === i + 1
                      ? styles.activePage
                      : styles.paginationButton
                  }
                >
                  {i + 1}
                </button>
              )
            )}

            {/* Next Button */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === Math.ceil(filteredLocations.length / pageSize)}
              className={styles.paginationButton}
            >
              &gt;
            </button>
          </div>
        </>
      ) : (
        <LocationsTree locations={filteredLocations} onViewDetail={handleViewDetails} />
      )}

      <ConfirmModal
        show={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        message="Are you sure you want to delete this container?"
        details={"Container ID : " + selectedLocationDeleteId}
      />

      <LocationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        locationId={selectedLocationId}
      />

      {/* ImageModal */}
      <ImageModal
        isOpen={isImageModalOpen}
        images={selectedImages}
        currentImageIndex={currentImageIndex}
        onClose={handleCloseModal}
        onNext={handleNextImage}
        onPrevious={handlePreviousImage}
      />

    </div>
  );
};

export default LocationTable;
